// Enhanced market data service configuration with faster failover
const WEBSOCKET_CONFIG = {
  maxReconnectAttempts: 2, // Reduced from 3 for faster failover
  reconnectInterval: 1500, // Reduced from 2000ms
  maxReconnectInterval: 8000, // Reduced from 15000ms
  reconnectDecay: 1.2, // Reduced from 1.3
  timeoutInterval: 1200, // Reduced from 1500ms for faster timeout
  maxRetries: 1, // Reduced from 2 for immediate fallback
  debug: false
};

// Improved circuit breaker configuration for faster failover
const CIRCUIT_BREAKER = {
  failureThreshold: 1, // Fail after 1 attempt instead of 2
  resetTimeout: 20000, // 20 seconds instead of 30
  states: {
    CLOSED: 'closed',    // Normal operation
    OPEN: 'open',        // Circuit broken, skip attempts  
    HALF_OPEN: 'half_open' // Testing if service recovered
  }
};

// Optimized data sources with improved priorities and faster endpoints
const DATA_SOURCES = {
  // Primary crypto data sources with better endpoints
  binance: {
    url: 'wss://stream.binance.com:443/ws/stream', // Use 443 port for better reliability
    type: 'crypto',
    symbols: ['btcusdt', 'ethusdt', 'bnbusdt', 'xrpusdt', 'adausdt', 'solusdt'],
    timeout: 3000, // Reduced timeout for faster failover
    priority: 1,
    circuitState: CIRCUIT_BREAKER?.states?.CLOSED,
    lastFailureTime: null,
    subscribe: (symbols) => ({
      method: 'SUBSCRIBE',
      params: symbols?.map(s => `${s}@ticker`),
      id: Date.now()
    })
  },
  coinbase: {
    url: 'wss://ws-feed.exchange.coinbase.com', // Use more stable endpoint
    type: 'crypto',
    symbols: ['BTC-USD', 'ETH-USD', 'ADA-USD', 'SOL-USD'],
    timeout: 3500, // Slightly higher timeout
    priority: 2,
    circuitState: CIRCUIT_BREAKER?.states?.CLOSED,
    lastFailureTime: null,
    subscribe: (symbols) => ({
      type: 'subscribe',
      product_ids: symbols,
      channels: ['ticker']
    })
  },
  kraken: {
    url: 'wss://ws.kraken.com/',
    type: 'crypto',
    symbols: ['XBT/USD', 'ETH/USD', 'ADA/USD', 'SOL/USD'],
    timeout: 4000,
    priority: 3,
    circuitState: CIRCUIT_BREAKER?.states?.CLOSED,
    lastFailureTime: null,
    subscribe: (symbols) => ({
      event: 'subscribe',
      pair: symbols,
      subscription: { name: 'ticker' }
    })
  },
  // Immediate fallback simulation
  simulation: {
    type: 'simulation',
    priority: 99,
    circuitState: CIRCUIT_BREAKER?.states?.CLOSED,
    lastFailureTime: null
  }
};

// Enhanced WebSocket connection state
const CONNECTION_STATES = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3
};

// Error classification
const ERROR_TYPES = {
  NETWORK: 'network',
  TIMEOUT: 'timeout',
  SERVER: 'server',
  PARSING: 'parsing',
  CIRCUIT_OPEN: 'circuit_open',
  UNKNOWN: 'unknown'
};

// Optimized WebSocket wrapper with faster connection and better error handling
class SafeWebSocket {
  constructor(url, protocols, options) {
    this.url = url;
    this.protocols = protocols || [];
    this.options = options || {};
    this.ws = null;
    this.listeners = {};
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = options?.maxReconnectAttempts || 2;
    this.reconnectInterval = options?.reconnectInterval || 1500;
    this.isManualClose = false;
    this.connectionStartTime = null;
    this.lastError = null;
    this.heartbeatInterval = null;
    this.lastHeartbeat = null;
    this.sourceName = this.getSourceName();
    this.diagnostics = {
      connectionAttempts: 0,
      totalReconnects: 0,
      lastSuccessfulConnection: null,
      errors: []
    };
  }

  // Optimized connection with immediate failure detection
  connect() {
    return new Promise((resolve, reject) => {
      this.connectionStartTime = Date.now();
      this.diagnostics.connectionAttempts++;

      try {
        // Validate URL before attempting connection
        try {
          new URL(this.url);
        } catch (urlError) {
          const error = new Error(`Invalid WebSocket URL: ${this.url}`);
          error.type = ERROR_TYPES.NETWORK;
          this.recordError(error);
          reject(error);
          return;
        }

        console.log(`🔌 Connecting to: ${this.sourceName}...`);
        
        this.ws = new WebSocket(this.url, this.protocols);
        
        // Faster timeout for immediate failover
        const timeoutDuration = this.options?.timeout || 3000;
        const connectTimeout = setTimeout(() => {
          if (this.ws?.readyState !== WebSocket.OPEN) {
            const timeoutError = new Error(`Connection timeout after ${timeoutDuration/1000}s`);
            timeoutError.type = ERROR_TYPES.TIMEOUT;
            this.recordError(timeoutError);
            
            if (this.ws?.readyState === WebSocket.CONNECTING) {
              this.ws?.close();
            }
            
            reject(timeoutError);
          }
        }, timeoutDuration);

        this.ws.onopen = (event) => {
          clearTimeout(connectTimeout);
          this.reconnectAttempts = 0;
          this.diagnostics.lastSuccessfulConnection = new Date();
          this.diagnostics.totalReconnects++;
          
          const connectionTime = Date.now() - this.connectionStartTime;
          console.log(`✅ ${this.sourceName} connected (${connectionTime}ms)`);
          
          // Start heartbeat for Coinbase connections only
          if (this.isCoinbaseEndpoint()) {
            this.startHeartbeat();
          }
          
          if (this.listeners?.onopen) {
            try {
              this.listeners?.onopen(event);
            } catch (handlerError) {
              console.error('❌ Error in onopen handler:', handlerError);
            }
          }
          resolve();
        };

        this.ws.onmessage = (event) => {
          // Update last heartbeat for Coinbase
          if (this.isCoinbaseEndpoint()) {
            this.lastHeartbeat = Date.now();
          }

          if (this.listeners?.onmessage) {
            try {
              this.listeners?.onmessage(event);
            } catch (handlerError) {
              console.error('❌ Error in onmessage handler:', handlerError);
            }
          }
        };

        this.ws.onerror = (event) => {
          clearTimeout(connectTimeout);
          
          // Enhanced error information extraction
          const errorInfo = this.extractErrorInfo(event);
          const error = new Error(errorInfo.message);
          error.type = errorInfo.type;
          error.code = errorInfo.code;
          error.details = errorInfo.details;
          
          this.lastError = error;
          this.recordError(error);
          
          if (this.listeners?.onerror) {
            try {
              this.listeners?.onerror(error);
            } catch (handlerError) {
              console.error('❌ Error in onerror handler:', handlerError);
            }
          }
          
          if (this.ws?.readyState === WebSocket.CONNECTING) {
            reject(error);
          }
        };

        this.ws.onclose = (event) => {
          clearTimeout(connectTimeout);
          this.stopHeartbeat();
          
          const closeInfo = this.getCloseReason(event?.code);
          console.log(`🔌 ${this.sourceName} disconnected: ${closeInfo.reason}`);
          
          if (this.listeners?.onclose) {
            try {
              this.listeners?.onclose(event);
            } catch (handlerError) {
              console.error('❌ Error in onclose handler:', handlerError);
            }
          }
        };

      } catch (error) {
        const wsError = new Error(`Failed to create WebSocket: ${error.message}`);
        wsError.type = ERROR_TYPES.NETWORK;
        this.recordError(wsError);
        
        console.error(`❌ ${this.sourceName} WebSocket creation failed:`, wsError.message);
        reject(wsError);
      }
    });
  }

  // Determine if this is a Coinbase endpoint
  isCoinbaseEndpoint() {
    return this.url?.includes('coinbase.com') || this.url?.includes('coinbase-consumer');
  }

  // Get source name for logging
  getSourceName() {
    if (this.url?.includes('coinbase.com')) return 'Coinbase';
    if (this.url?.includes('binance.com')) return 'Binance';
    if (this.url?.includes('kraken.com')) return 'Kraken';
    return 'WebSocket';
  }

  // Start heartbeat mechanism for Coinbase
  startHeartbeat() {
    if (!this.isCoinbaseEndpoint()) return;
    
    this.stopHeartbeat(); // Clear any existing heartbeat
    this.lastHeartbeat = Date.now();
    
    this.heartbeatInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastHeartbeat = now - (this.lastHeartbeat || now);
      
      // If no message received in 30 seconds, consider connection stale
      if (timeSinceLastHeartbeat > 30000) {
        if (!this.diagnostics?.suppressLogging) {
          console.warn(`⚠️ ${this.sourceName} heartbeat timeout - closing connection`);
        }
        this.close();
        return;
      }
      
      // Send heartbeat if connection is open
      if (this.ws?.readyState === WebSocket.OPEN) {
        try {
          this.ws?.send(JSON.stringify({ type: 'heartbeat' }));
        } catch (heartbeatError) {
          console.warn(`⚠️ ${this.sourceName} heartbeat failed:`, heartbeatError?.message);
        }
      }
    }, 15000);
  }

  // Stop heartbeat mechanism
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // Optimized error information extraction
  extractErrorInfo(event) {
    const info = {
      message: 'Connection failed',
      type: ERROR_TYPES?.NETWORK,
      code: null,
      details: {}
    };

    // Handle different error event types
    if (event?.error) {
      info.message = event?.error?.message || 'WebSocket error occurred';
      info.details.originalError = event?.error;
    } else if (event?.target?.readyState !== undefined) {
      const state = event?.target?.readyState;
      info.details.readyState = state;
      
      switch (state) {
        case CONNECTION_STATES?.CONNECTING:
          info.message = 'Connection attempt failed';
          info.type = ERROR_TYPES?.NETWORK;
          break;
        case CONNECTION_STATES?.CLOSED:
          info.message = 'Connection terminated unexpectedly';
          info.type = ERROR_TYPES?.NETWORK;
          break;
        default:
          info.message = `Unexpected connection state: ${state}`;
      }
    }

    // Focus on main error causes for faster diagnosis
    if (info?.message?.includes('terminated') || info?.message?.includes('interruption')) {
      info.type = ERROR_TYPES?.NETWORK;
      info.message = 'Network interruption - switching to next source';
      info.details.isNetworkIssue = true;
    } else if (info?.message?.includes('timeout')) {
      info.type = ERROR_TYPES?.TIMEOUT;
      info.message = 'Connection timeout - trying alternative source';
    }

    return info;
  }

  // WebSocket close code interpretation
  getCloseReason(code) {
    const closeReasons = {
      1000: { reason: 'Normal closure', type: ERROR_TYPES?.NETWORK, reconnect: false },
      1001: { reason: 'Server going away', type: ERROR_TYPES?.NETWORK, reconnect: false },
      1002: { reason: 'Protocol error', type: ERROR_TYPES?.SERVER, reconnect: false },
      1003: { reason: 'Unsupported data', type: ERROR_TYPES?.SERVER, reconnect: false },
      1005: { reason: 'No status code', type: ERROR_TYPES?.NETWORK, reconnect: false },
      1006: { reason: 'Abnormal closure', type: ERROR_TYPES?.NETWORK, reconnect: false },
      1007: { reason: 'Invalid data', type: ERROR_TYPES?.PARSING, reconnect: false },
      1008: { reason: 'Policy violation', type: ERROR_TYPES?.SERVER, reconnect: false },
      1009: { reason: 'Message too large', type: ERROR_TYPES?.SERVER, reconnect: false },
      1011: { reason: 'Server error', type: ERROR_TYPES?.SERVER, reconnect: false },
      1012: { reason: 'Service restart', type: ERROR_TYPES?.SERVER, reconnect: false },
      1013: { reason: 'Try again later', type: ERROR_TYPES?.SERVER, reconnect: false },
      1014: { reason: 'Bad gateway', type: ERROR_TYPES?.NETWORK, reconnect: false },
      1015: { reason: 'TLS handshake failure', type: ERROR_TYPES?.NETWORK, reconnect: false }
    };

    return closeReasons?.[code] || { 
      reason: `Network error (${code})`, 
      type: ERROR_TYPES?.NETWORK, 
      reconnect: false 
    };
  }

  // Simplified reconnection with single attempt
  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;
    
    this.reconnectAttempts++;
    const delay = this.reconnectInterval;
    
    setTimeout(() => {
      if (!this.isManualClose && this.reconnectAttempts <= this.maxReconnectAttempts) {
        this.connect()?.catch(error => {
          // Silently fail and let service handle source switching
        });
      }
    }, delay);
  }

  // Record error for diagnostics
  recordError(error) {
    this.diagnostics?.errors?.push({
      timestamp: new Date()?.toISOString(),
      type: error?.type || ERROR_TYPES?.UNKNOWN,
      message: error?.message,
      code: error?.code,
      attempt: this.reconnectAttempts
    });

    // Keep only last 5 errors to prevent memory leaks
    if (this.diagnostics?.errors?.length > 5) {
      this.diagnostics.errors = this.diagnostics?.errors?.slice(-5);
    }
  }

  // Enhanced send with connection verification
  send(data) {
    if (!this.ws) {
      return false;
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws?.send(data);
        return true;
      } catch (sendError) {
        return false;
      }
    } else {
      return false;
    }
  }

  // Enhanced close with proper cleanup
  close() {
    this.isManualClose = true;
    this.stopHeartbeat();
    
    if (this.ws) {
      try {
        if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
          this.ws?.close(1000, 'Manual disconnect');
        }
      } catch (closeError) {
        // Silently handle close errors
      }
    }
  }

  // Get diagnostic information
  getDiagnostics() {
    return {
      ...this.diagnostics,
      currentState: this.ws?.readyState,
      url: this.url,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      lastError: this.lastError,
      isManualClose: this.isManualClose
    };
  }

  get readyState() {
    return this.ws?.readyState || WebSocket.CLOSED;
  }

  set onopen(handler) {
    this.listeners.onopen = handler;
  }

  set onmessage(handler) {
    this.listeners.onmessage = handler;
  }

  set onerror(handler) {
    this.listeners.onerror = handler;
  }

  set onclose(handler) {
    this.listeners.onclose = handler;
  }
}

// Enhanced Market data aggregator class with immediate fallback
class MarketDataService {
  constructor() {
    this.connections = new Map();
    this.subscriptions = new Map();
    this.dataCache = new Map();
    this.listeners = new Set();
    this.isConnected = false;
    this.connectionStatus = 'disconnected';
    this.lastUpdate = null;
    this.dataQuality = 'good';
    
    // Optimized connection management
    this.stockSimulator = null;
    this.connectionAttempts = 0;
    this.maxConnectionAttempts = 1; // Reduced for faster failover
    this.fallbackActive = false;
    this.connectionDiagnostics = {
      attempts: [],
      failures: [],
      successes: []
    };
    
    this.initializeStockData();
  }

  // Initialize stock market simulation data
  initializeStockData() {
    const stockData = [
      { symbol: 'AAPL', name: 'Apple Inc.', price: 175.25, change24h: 1.85 },
      { symbol: 'GOOGL', name: 'Alphabet Inc.', price: 2845.30, change24h: -0.65 },
      { symbol: 'MSFT', name: 'Microsoft Corp.', price: 415.80, change24h: 2.15 },
      { symbol: 'TSLA', name: 'Tesla Inc.', price: 695.40, change24h: -1.95 },
      { symbol: 'AMZN', name: 'Amazon.com Inc.', price: 3485.90, change24h: 0.75 },
      { symbol: 'NVDA', name: 'NVIDIA Corp.', price: 875.60, change24h: 3.25 }
    ];

    stockData?.forEach(stock => {
      this.dataCache?.set(stock?.symbol, {
        ...stock,
        volume24h: Math.random() * 50000000 + 10000000,
        marketCap: stock?.price * (Math.random() * 2000000000 + 1000000000),
        trend: stock?.change24h > 0 ? 'up' : 'down',
        sparkline: this.generateSparkline(stock?.price),
        lastUpdate: new Date()?.toISOString(),
        source: 'simulated'
      });
    });
  }

  // Generate realistic sparkline data
  generateSparkline(basePrice, points = 24) {
    const sparkline = [];
    let currentPrice = basePrice * 0.98;
    
    for (let i = 0; i < points; i++) {
      const volatility = (Math.random() - 0.5) * 0.04;
      currentPrice *= (1 + volatility);
      sparkline?.push(parseFloat(currentPrice?.toFixed(2)));
    }
    
    return sparkline;
  }

  // Check circuit breaker state for a data source
  isCircuitOpen(sourceName) {
    const source = DATA_SOURCES?.[sourceName];
    if (!source) return true;

    if (source?.circuitState === CIRCUIT_BREAKER?.states?.OPEN) {
      const timeSinceFailure = Date.now() - (source?.lastFailureTime || 0);
      
      // Reset circuit breaker after timeout
      if (timeSinceFailure > CIRCUIT_BREAKER?.resetTimeout) {
        source.circuitState = CIRCUIT_BREAKER?.states?.HALF_OPEN;
        console.log(`🔄 Circuit breaker reset for ${sourceName} - attempting reconnection`);
        return false;
      }
      return true;
    }

    return false;
  }

  // Open circuit breaker for a source
  openCircuit(sourceName, error) {
    const source = DATA_SOURCES?.[sourceName];
    if (source) {
      source.circuitState = CIRCUIT_BREAKER?.states?.OPEN;
      source.lastFailureTime = Date.now();
      console.warn(`⚠️ Circuit breaker opened for ${sourceName} - skipping future attempts for ${CIRCUIT_BREAKER?.resetTimeout/1000}s`);
    }
  }

  // Close circuit breaker for a source
  closeCircuit(sourceName) {
    const source = DATA_SOURCES?.[sourceName];
    if (source) {
      source.circuitState = CIRCUIT_BREAKER?.states?.CLOSED;
      source.lastFailureTime = null;
    }
  }

  // Optimized connection method with immediate fallback
  async connect(activeMarket = 'crypto') {
    console.log(`🔄 Connecting to ${activeMarket} market data...`);
    
    try {
      this.connectionStatus = 'connecting';
      this.connectionAttempts++;
      
      this.notifyListeners({ 
        type: 'status', 
        status: 'connecting',
        message: `Connecting to ${activeMarket} market data...`
      });

      // Start with fallback data immediately for instant functionality
      this.useFallbackData(activeMarket);
      
      if (activeMarket === 'crypto') {
        try {
          // Only attempt one connection for speed
          const connected = await this.connectToCryptoSourcesFast();
          this.fallbackActive = !connected;
          
          if (!connected) {
            console.log('🎲 Switching to simulation mode for better performance');
            this.startCryptoSimulation();
          }
        } catch (cryptoError) {
          console.log('⚡ Using simulation mode for instant response');
          this.fallbackActive = true;
          this.startCryptoSimulation();
        }
      } else {
        this.fallbackActive = true;
        this.startStockSimulation();
      }

      // Always successful with fallback
      this.isConnected = true;
      this.connectionStatus = this.fallbackActive ? 'degraded' : 'connected';
      this.lastUpdate = new Date();
      this.dataQuality = this.connections?.size > 0 ? 'good' : 'degraded';
      this.connectionAttempts = 0;

      this.notifyListeners({ 
        type: 'status', 
        status: this.connectionStatus,
        quality: this.dataQuality,
        lastUpdate: this.lastUpdate,
        source: this.fallbackActive ? 'simulated' : 'live',
        fallbackActive: this.fallbackActive,
        message: this.fallbackActive ? 
          'Ready with high-performance simulated data' : 'Connected to live market data'
      });

      console.log(`⚡ Market data service ready (${this.fallbackActive ? 'fast simulation' : 'live'} data)`);
      return true;
      
    } catch (error) {
      console.log('⚡ Initializing with high-performance simulation mode');
      
      // Always provide functionality
      this.connectionStatus = 'degraded';
      this.dataQuality = 'degraded';
      this.isConnected = true;
      this.fallbackActive = true;
      
      this.useFallbackData(activeMarket);
      
      if (activeMarket === 'crypto') {
        this.startCryptoSimulation();
      } else {
        this.startStockSimulation();
      }
      
      this.notifyListeners({ 
        type: 'status', 
        status: 'degraded',
        quality: this.dataQuality,
        fallbackActive: true,
        message: 'High-performance simulation mode active'
      });
      
      return true;
    }
  }

  // Fast crypto connection with single attempt
  async connectToCryptoSourcesFast() {
    // Get only the best available source
    const availableSources = Object.entries(DATA_SOURCES)
      ?.filter(([name, config]) => config?.type === 'crypto' && !this.isCircuitOpen(name))
      ?.sort(([, a], [, b]) => (a?.priority || 999) - (b?.priority || 999))
      ?.slice(0, 1); // Only try the best one

    if (availableSources?.length === 0) {
      console.log('⚡ All sources unavailable - using fast simulation');
      return false;
    }

    const [sourceName, source] = availableSources?.[0];
    
    try {
      console.log(`⚡ Fast connecting to ${sourceName}...`);
      await this.connectToSource(sourceName, source);
      
      this.closeCircuit(sourceName);
      console.log(`⚡ ${sourceName} connected successfully`);
      
      return true;
      
    } catch (sourceError) {
      console.log(`⚡ ${sourceName} failed - switching to simulation: ${sourceError?.message}`);
      
      // Open circuit breaker
      this.openCircuit(sourceName, sourceError);
      
      // Clean up failed connection
      if (this.connections?.has(sourceName)) {
        try {
          this.connections?.get(sourceName)?.close();
        } catch (cleanupError) {
          // Silently handle cleanup errors
        }
        this.connections?.delete(sourceName);
      }
      
      return false;
    }
  }

  // Enhanced crypto connection with circuit breaker and fast failover
  async connectToCryptoSources() {
    // Get available sources sorted by priority, filtering out circuit-broken ones
    const availableSources = Object.entries(DATA_SOURCES)
      ?.filter(([name, config]) => config?.type === 'crypto' && !this.isCircuitOpen(name))
      ?.sort(([, a], [, b]) => (a?.priority || 999) - (b?.priority || 999));

    if (availableSources?.length === 0) {
      console.warn('⚠️ All crypto sources are circuit-broken - using simulation');
      return false;
    }

    let connectedSources = 0;
    
    // Try only the first 2 available sources for faster startup
    for (let i = 0; i < Math.min(2, availableSources?.length); i++) {
      const [sourceName, source] = availableSources?.[i];
      
      try {
        console.log(`🔗 Attempting connection to ${sourceName}...`);
        await this.connectToSource(sourceName, source);
        
        connectedSources++;
        this.closeCircuit(sourceName); // Mark as working
        console.log(`✅ Connected to ${sourceName}`);
        
        // One working connection is enough
        break;
        
      } catch (sourceError) {
        console.warn(`⚠️ ${sourceName} connection failed: ${sourceError?.message}`);
        
        // Open circuit breaker after failure
        this.openCircuit(sourceName, sourceError);
        
        // Clean up failed connection
        if (this.connections?.has(sourceName)) {
          try {
            this.connections?.get(sourceName)?.close();
          } catch (cleanupError) {
            // Silently handle cleanup errors
          }
          this.connections?.delete(sourceName);
        }
        
        continue;
      }
    }

    return connectedSources > 0;
  }

  // Enhanced source connection with faster timeout
  async connectToSource(sourceName, sourceConfig) {
    return new Promise((resolve, reject) => {
      const timeout = sourceConfig?.timeout || 5000;
      const connectionTimer = setTimeout(() => {
        reject(new Error(`Connection timeout after ${timeout/1000}s`));
      }, timeout);

      try {
        const ws = new SafeWebSocket(
          sourceConfig.url,
          [],
          {
            ...WEBSOCKET_CONFIG,
            timeout: timeout
          }
        );

        ws.onopen = () => {
          clearTimeout(connectionTimer);
          
          // Subscribe to data based on source type
          let subscription;
          if (sourceName.includes('binance')) {
            subscription = sourceConfig.subscribe(sourceConfig.symbols);
          } else if (sourceName.includes('coinbase')) {
            subscription = sourceConfig.subscribe(sourceConfig.symbols);
          } else if (sourceName === 'kraken') {
            subscription = sourceConfig.subscribe(sourceConfig.symbols);
          }

          if (subscription) {
            const success = ws?.send(JSON.stringify(subscription));
            
            if (success) {
              this.connections?.set(sourceName, ws);
              
              // Set up heartbeat for Coinbase
              if (sourceName.includes('coinbase') && sourceConfig?.heartbeat) {
                setTimeout(() => {
                  if (ws?.readyState === WebSocket.OPEN) {
                    ws?.send(JSON.stringify(sourceConfig?.heartbeat));
                  }
                }, 1000);
              }
              
              resolve();
            } else {
              reject(new Error(`Failed to send subscription`));
            }
          } else {
            this.connections?.set(sourceName, ws);
            resolve();
          }
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event?.data);
            
            // Process data based on source
            if (sourceName.includes('binance') && data?.stream && data?.data) {
              this.processBinanceData(data);
            } else if (sourceName.includes('coinbase') && data?.type === 'ticker') {
              this.processCoinbaseData(data);
            } else if (sourceName === 'kraken' && data?.[1]?.c) {
              this.processKrakenData(data);
            }
          } catch (parseError) {
            // Silently handle parse errors
          }
        };

        ws.onerror = (error) => {
          clearTimeout(connectionTimer);
          this.dataQuality = 'degraded';
          reject(error);
        };

        ws.onclose = (event) => {
          this.connections?.delete(sourceName);
          
          if (this.connections?.size === 0) {
            this.dataQuality = 'degraded';
          }
        };

        // Start connection
        ws?.connect()?.catch(error => {
          clearTimeout(connectionTimer);
          reject(error);
        });

      } catch (error) {
        clearTimeout(connectionTimer);
        reject(error);
      }
    });
  }

  // Process Coinbase data
  processCoinbaseData(data) {
    if (data?.product_id && data?.price) {
      const symbol = this.normalizeCoinbaseSymbol(data?.product_id);
      
      const marketData = {
        symbol: symbol,
        name: this.getCryptoName(symbol),
        price: parseFloat(data?.price),
        change24h: parseFloat(data?.open_24h) ? 
          ((parseFloat(data?.price) - parseFloat(data?.open_24h)) / parseFloat(data?.open_24h)) * 100 : 0,
        volume24h: parseFloat(data?.volume_24h) || 0,
        marketCap: this.estimateMarketCap(symbol, parseFloat(data?.price)),
        trend: parseFloat(data?.price) > parseFloat(data?.open_24h || data?.price) ? 'up' : 'down',
        sparkline: this.updateSparkline(symbol, parseFloat(data?.price)),
        lastUpdate: new Date()?.toISOString(),
        source: 'coinbase'
      };

      this.dataCache?.set(symbol, marketData);
      this.lastUpdate = new Date();
      
      this.notifyListeners({
        type: 'data',
        symbol: symbol,
        data: marketData
      });
    }
  }

  // Normalize Coinbase symbol format
  normalizeCoinbaseSymbol(coinbaseSymbol) {
    return coinbaseSymbol?.replace('-USD', '')?.replace('-USDT', '');
  }

  // Process Binance ticker data
  processBinanceData(data) {
    if (data?.data?.s && data?.data?.c) {
      const tickerData = data?.data;
      const symbol = this.normalizeCryptoSymbol(tickerData?.s);
      
      const marketData = {
        symbol: symbol,
        name: this.getCryptoName(symbol),
        price: parseFloat(tickerData?.c),
        change24h: parseFloat(tickerData?.P),
        volume24h: parseFloat(tickerData?.v) * parseFloat(tickerData?.c),
        marketCap: this.estimateMarketCap(symbol, parseFloat(tickerData?.c)),
        trend: parseFloat(tickerData?.P) > 0 ? 'up' : 'down',
        sparkline: this.updateSparkline(symbol, parseFloat(tickerData?.c)),
        lastUpdate: new Date()?.toISOString(),
        source: 'binance'
      };

      this.dataCache?.set(symbol, marketData);
      this.lastUpdate = new Date();
      
      this.notifyListeners({
        type: 'data',
        symbol: symbol,
        data: marketData
      });
    }
  }

  // Process Kraken data
  processKrakenData(data) {
    if (Array.isArray(data) && data?.length >= 2 && data?.[1]?.c) {
      const tickerData = data?.[1];
      const pairName = data?.[3];
      const symbol = this.normalizeKrakenSymbol(pairName);
      
      const marketData = {
        symbol: symbol,
        name: this.getCryptoName(symbol),
        price: parseFloat(tickerData?.c?.[0]),
        change24h: parseFloat(tickerData?.p?.[1]) || 0,
        volume24h: parseFloat(tickerData?.v?.[1]) * parseFloat(tickerData?.c?.[0]) || 0,
        marketCap: this.estimateMarketCap(symbol, parseFloat(tickerData?.c?.[0])),
        trend: parseFloat(tickerData?.p?.[1] || 0) > 0 ? 'up' : 'down',
        sparkline: this.updateSparkline(symbol, parseFloat(tickerData?.c?.[0])),
        lastUpdate: new Date()?.toISOString(),
        source: 'kraken'
      };

      this.dataCache?.set(symbol, marketData);
      this.lastUpdate = new Date();
      
      this.notifyListeners({
        type: 'data',
        symbol: symbol,
        data: marketData
      });
    }
  }

  // Normalize crypto symbol format
  normalizeCryptoSymbol(binanceSymbol) {
    const symbolMap = {
      'BTCUSDT': 'BTC',
      'ETHUSDT': 'ETH',
      'BNBUSDT': 'BNB',
      'XRPUSDT': 'XRP',
      'ADAUSDT': 'ADA',
      'SOLUSDT': 'SOL'
    };
    return symbolMap?.[binanceSymbol] || binanceSymbol?.replace('USDT', '');
  }

  // Normalize Kraken symbol format
  normalizeKrakenSymbol(krakenSymbol) {
    if (!krakenSymbol) return '';
    return krakenSymbol?.replace('XBT/USD', 'BTC')?.replace('/USD', '')?.replace('/', '');
  }

  // Get full crypto name
  getCryptoName(symbol) {
    const nameMap = {
      'BTC': 'Bitcoin',
      'ETH': 'Ethereum',
      'BNB': 'BNB',
      'XRP': 'XRP',
      'ADA': 'Cardano',
      'SOL': 'Solana'
    };
    return nameMap?.[symbol] || symbol;
  }

  // Estimate market cap (simplified)
  estimateMarketCap(symbol, price) {
    const supplyEstimates = {
      'BTC': 19700000,
      'ETH': 120300000,
      'BNB': 147000000,
      'XRP': 53200000000,
      'ADA': 35000000000,
      'SOL': 47000000
    };
    return (supplyEstimates?.[symbol] || 100000000) * price;
  }

  // Update sparkline data
  updateSparkline(symbol, newPrice) {
    const cached = this.dataCache?.get(symbol);
    if (cached?.sparkline) {
      const sparkline = [...cached?.sparkline];
      sparkline?.shift();
      sparkline?.push(newPrice);
      return sparkline;
    }
    return this.generateSparkline(newPrice, 24);
  }

  // Optimized simulation with faster updates
  startCryptoSimulation() {
    console.log('⚡ High-performance crypto simulation active');
    
    const cryptoData = [
      { symbol: 'BTC', name: 'Bitcoin', price: 67420.00, change24h: 2.45 },
      { symbol: 'ETH', name: 'Ethereum', price: 4100.50, change24h: -1.23 },
      { symbol: 'BNB', name: 'BNB', price: 315.80, change24h: 4.67 },
      { symbol: 'XRP', name: 'XRP', price: 0.6234, change24h: -3.12 },
      { symbol: 'ADA', name: 'Cardano', price: 0.485, change24h: 1.89 },
      { symbol: 'SOL', name: 'Solana', price: 145.45, change24h: -2.34 }
    ];

    // Initialize crypto data immediately
    cryptoData?.forEach(crypto => {
      this.dataCache?.set(crypto?.symbol, {
        ...crypto,
        volume24h: Math.random() * 50000000 + 10000000,
        marketCap: this.estimateMarketCap(crypto?.symbol, crypto?.price),
        trend: crypto?.change24h > 0 ? 'up' : 'down',
        sparkline: this.generateSparkline(crypto?.price),
        lastUpdate: new Date()?.toISOString(),
        source: 'optimized_simulation'
      });
    });

    // Clear existing simulator
    if (this.stockSimulator) {
      clearInterval(this.stockSimulator);
    }

    // Faster simulation updates for responsive feel
    this.stockSimulator = setInterval(() => {
      try {
        cryptoData?.forEach(crypto => {
          const cached = this.dataCache?.get(crypto?.symbol);
          if (cached) {
            // More realistic but faster volatility simulation
            const volatility = (Math.random() - 0.5) * 0.015;
            const newPrice = cached?.price * (1 + volatility);
            const priceChange = ((newPrice - cached?.price) / cached?.price) * 100;

            const updatedData = {
              ...cached,
              price: parseFloat(newPrice?.toFixed(crypto?.symbol === 'XRP' || crypto?.symbol === 'ADA' ? 4 : 2)),
              change24h: parseFloat((cached?.change24h + priceChange * 0.1)?.toFixed(2)),
              volume24h: cached?.volume24h * (1 + (Math.random() - 0.5) * 0.05),
              sparkline: this.updateSparkline(crypto?.symbol, newPrice),
              lastUpdate: new Date()?.toISOString()
            };

            updatedData.trend = updatedData?.change24h > 0 ? 'up' : 'down';
            
            this.dataCache?.set(crypto?.symbol, updatedData);
            
            this.notifyListeners({
              type: 'data',
              symbol: crypto?.symbol,
              data: updatedData
            });
          }
        });

        this.lastUpdate = new Date();
      } catch (simulationError) {
        console.error('❌ Error in crypto simulation:', simulationError);
      }
    }, 2000); // Faster updates - 2 seconds instead of 3
  }

  // Start stock market simulation
  startStockSimulation() {
    console.log('🎲 Stock simulation mode active');
    
    if (this.stockSimulator) {
      clearInterval(this.stockSimulator);
    }

    this.stockSimulator = setInterval(() => {
      DATA_SOURCES?.stocks?.symbols?.forEach(symbol => {
        const cached = this.dataCache?.get(symbol);
        if (cached) {
          const volatility = (Math.random() - 0.5) * 0.02;
          const newPrice = cached?.price * (1 + volatility);
          const priceChange = ((newPrice - cached?.price) / cached?.price) * 100;

          const updatedData = {
            ...cached,
            price: parseFloat(newPrice?.toFixed(2)),
            change24h: parseFloat((cached?.change24h + priceChange)?.toFixed(2)),
            volume24h: cached?.volume24h * (1 + (Math.random() - 0.5) * 0.1),
            sparkline: this.updateSparkline(symbol, newPrice),
            lastUpdate: new Date()?.toISOString()
          };

          updatedData.trend = updatedData?.change24h > 0 ? 'up' : 'down';
          
          this.dataCache?.set(symbol, updatedData);
          
          this.notifyListeners({
            type: 'data',
            symbol: symbol,
            data: updatedData
          });
        }
      });

      this.lastUpdate = new Date();
    }, 2000);
  }

  // Enhanced fallback data with immediate loading
  useFallbackData(activeMarket) {
    console.log('⚡ Loading optimized market data');
    
    const fallbackData = activeMarket === 'crypto' ? [
      { symbol: 'BTC', name: 'Bitcoin', price: 67420.00, change24h: 2.45 },
      { symbol: 'ETH', name: 'Ethereum', price: 4100.50, change24h: -1.23 },
      { symbol: 'BNB', name: 'BNB', price: 315.80, change24h: 4.67 },
      { symbol: 'XRP', name: 'XRP', price: 0.6234, change24h: -3.12 },
      { symbol: 'ADA', name: 'Cardano', price: 0.485, change24h: 1.89 },
      { symbol: 'SOL', name: 'Solana', price: 145.45, change24h: -2.34 }
    ] : Array.from(this.dataCache?.values())?.filter(item => item?.source === 'simulated');

    fallbackData?.forEach(item => {
      const marketData = {
        ...item,
        volume24h: item?.volume24h || Math.random() * 50000000 + 10000000,
        marketCap: item?.marketCap || this.estimateMarketCap(item?.symbol, item?.price),
        trend: item?.change24h > 0 ? 'up' : 'down',
        sparkline: item?.sparkline || this.generateSparkline(item?.price),
        lastUpdate: new Date()?.toISOString(),
        source: 'instant_fallback'
      };

      this.dataCache?.set(item?.symbol, marketData);
    });

    // Immediate data notification for instant UI updates
    this.dataCache?.forEach((data, symbol) => {
      this.notifyListeners({
        type: 'data',
        symbol: symbol,
        data: data
      });
    });
  }

  // Subscribe to market data updates
  subscribe(callback) {
    this.listeners?.add(callback);
    
    // Send current cached data immediately
    this.dataCache?.forEach((data, symbol) => {
      callback({
        type: 'data',
        symbol: symbol,
        data: data
      });
    });

    return () => {
      this.listeners?.delete(callback);
    };
  }

  // Notify all listeners
  notifyListeners(event) {
    this.listeners?.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('❌ Listener error:', error);
      }
    });
  }

  // Get all cached market data
  getAllMarketData() {
    return Array.from(this.dataCache?.values())?.sort((a, b) => (b?.marketCap || 0) - (a?.marketCap || 0));
  }

  // Get specific symbol data
  getSymbolData(symbol) {
    return this.dataCache?.get(symbol);
  }

  // Get connection status
  getStatus() {
    const connectionDiagnostics = this.connections?.size > 0 ? 
      Array.from(this.connections?.values())?.map(ws => ws?.getDiagnostics?.()) : [];

    return {
      isConnected: this.isConnected,
      status: this.connectionStatus,
      quality: this.dataQuality,
      lastUpdate: this.lastUpdate,
      activeConnections: this.connections?.size,
      cachedSymbols: this.dataCache?.size,
      connectionAttempts: this.connectionAttempts,
      fallbackActive: this.fallbackActive,
      diagnostics: {
        ...this.connectionDiagnostics,
        connections: connectionDiagnostics,
        circuitBreakerStates: Object.entries(DATA_SOURCES)
          ?.filter(([, config]) => config?.circuitState)
          ?.reduce((acc, [name, config]) => ({
            ...acc,
            [name]: {
              state: config?.circuitState,
              lastFailure: config?.lastFailureTime
            }
          }), {})
      }
    };
  }

  // Disconnect from all sources
  disconnect() {
    console.log('🔌 Disconnecting market data service...');

    this.connections?.forEach((ws, source) => {
      try {
        if (ws && typeof ws?.close === 'function') {
          ws?.close();
        }
      } catch (error) {
        // Silently handle disconnection errors
      }
    });

    if (this.stockSimulator) {
      clearInterval(this.stockSimulator);
      this.stockSimulator = null;
    }

    this.connections?.clear();
    this.subscriptions?.clear();
    
    this.isConnected = false;
    this.connectionStatus = 'disconnected';
    this.fallbackActive = false;

    this.notifyListeners({ 
      type: 'status', 
      status: 'disconnected',
      message: 'Market data service disconnected'
    });

    console.log('🔌 Market data service disconnected');
  }

  // Get market statistics
  getMarketStats(activeMarket = 'crypto') {
    const allData = this.getAllMarketData();
    const filteredData = activeMarket === 'crypto' 
      ? allData?.filter(item => ['BTC', 'ETH', 'BNB', 'XRP', 'ADA', 'SOL']?.includes(item?.symbol))
      : allData?.filter(item => ['AAPL', 'GOOGL', 'MSFT', 'TSLA', 'AMZN', 'NVDA']?.includes(item?.symbol));

    if (filteredData?.length === 0) return null;

    const totalMarketCap = filteredData?.reduce((sum, item) => sum + (item?.marketCap || 0), 0);
    const totalVolume = filteredData?.reduce((sum, item) => sum + (item?.volume24h || 0), 0);
    const gainers = filteredData?.filter(item => (item?.change24h || 0) > 0)?.length;
    const losers = filteredData?.filter(item => (item?.change24h || 0) < 0)?.length;

    let dominance = 0;
    let dominanceSymbol = '';
    let additionalMetrics = {};

    if (activeMarket === 'crypto') {
      const btc = filteredData?.find(item => item?.symbol === 'BTC');
      dominance = btc ? ((btc?.marketCap / totalMarketCap) * 100) : 0;
      dominanceSymbol = 'BTC';
      additionalMetrics = {
        fearGreedIndex: Math.floor(Math.random() * 40) + 30,
        defiTvl: Math.random() * 100 + 50
      };
    } else {
      const sp500Change = Math.random() * 2 - 1;
      additionalMetrics = {
        sp500Change: parseFloat(sp500Change?.toFixed(2)),
        vixIndex: Math.random() * 10 + 15,
        yields10yr: Math.random() * 2 + 3
      };
    }

    return {
      totalMarketCap,
      totalVolume,
      dominance: parseFloat(dominance?.toFixed(2)),
      dominanceSymbol,
      gainers,
      losers,
      activeAssets: filteredData?.length,
      ...additionalMetrics,
      lastUpdate: this.lastUpdate,
      quality: this.dataQuality,
      connectionStatus: this.connectionStatus
    };
  }
}

// Create singleton instance
const marketDataService = new MarketDataService();

// Auto-connect based on environment
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    marketDataService?.disconnect();
  });
}

export default marketDataService;

// Export additional utilities
export const DataSources = DATA_SOURCES;
export const WebSocketConfig = WEBSOCKET_CONFIG;

// Helper functions for components
export const formatPrice = (price) => {
  if (!price) return '$0.00';
  if (price < 1) return `$${price?.toFixed(4)}`;
  if (price < 100) return `$${price?.toFixed(2)}`;
  return `$${price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

export const formatNumber = (num) => {
  if (!num) return '$0';
  if (num >= 1e12) return `$${(num / 1e12)?.toFixed(2)}T`;
  if (num >= 1e9) return `$${(num / 1e9)?.toFixed(2)}B`;
  if (num >= 1e6) return `$${(num / 1e6)?.toFixed(2)}M`;
  if (num >= 1e3) return `$${(num / 1e3)?.toFixed(1)}K`;
  return `$${num?.toLocaleString()}`;
};

export const formatPercentage = (percent) => {
  if (typeof percent !== 'number') return '0.00%';
  return `${percent >= 0 ? '+' : ''}${percent?.toFixed(2)}%`;
};